import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../services/api';
import DealSlipViewModal from '../components/DealSlipViewModal';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import GsecFinalApprovedLettersPanel from '../components/GsecFinalApprovedLettersPanel';
import LimitExceededNotice from '../components/LimitExceededNotice';
import { fetchSystemDayYmd } from '../utils/recentDealsDate';

export default function BackOfficeVerifierGsecBlotter() {
  const [transactions, setTransactions] = useState([]);
  const [allRecentTransactions, setAllRecentTransactions] = useState([]);
  const [systemDayYmd, setSystemDayYmd] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectingTxId, setRejectingTxId] = useState(null);
  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [documentTransaction, setDocumentTransaction] = useState(null);
  const [limitExceededInfo, setLimitExceededInfo] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const [response, day] = await Promise.all([
        axios.get(`${API_BASE_URL}/isin-master/gsec/recent`),
        fetchSystemDayYmd()
      ]);
      const all = response.data.data || [];
      setAllRecentTransactions(all);
      setSystemDayYmd(day || '');
      // Filter for back office verifier approval level (current_approval_level = 'back_office_verifier' or 2)
      const filtered = all.filter(tx => 
        (tx.current_approval_level === 'back_office_verifier' || tx.current_approval_level === 2) && 
        tx.status === 'pending'
      );
      setTransactions(filtered);
    } catch (err) {
      setError('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setApprovingId(id);
    setError('');
    setSuccess('');
    setLimitExceededInfo(null);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.put(`${API_BASE_URL}/isin-master/gsec/${id}/status`, {
        status: 'approved',
        userId: user.id
      }, { headers: getAuthHeaders() });
      setSuccess('Transaction approved successfully');
      fetchTransactions();
    } catch (err) {
      if (err.response?.data?.limitExceeded) {
        setLimitExceededInfo(err.response.data);
      } else {
        setError('Failed to approve transaction: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (id) => {
    if (!rejectComment.trim()) {
      setError('Comment is required when rejecting a transaction');
      return;
    }
    setRejectingId(id);
    setError('');
    setSuccess('');
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.put(`${API_BASE_URL}/isin-master/gsec/${id}/status`, {
        status: 'rejected',
        comment: rejectComment,
        userId: user.id
      }, { headers: getAuthHeaders() });
      setSuccess('Transaction rejected successfully');
      setRejectComment('');
      setRejectingTxId(null);
      fetchTransactions();
    } catch (err) {
      setError('Failed to reject transaction: ' + (err.response?.data?.message || err.message));
    } finally {
      setRejectingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPrice = (value) => {
    if (!value && value !== 0) return '';
    return Number(value).toFixed(4);
  };

  return (
    <div className="sister-blotter">
      <h2 style={{ fontSize: '2rem', marginBottom: '16px' }}>Back Office Verifier GSec Blotter</h2>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red', marginBottom: 16, padding: '10px', background: '#ffe6e6', borderRadius: '4px' }}>{error}</div>}
      {success && <div style={{ color: 'green', marginBottom: 16, padding: '10px', background: '#e6ffe6', borderRadius: '4px' }}>{success}</div>}
      <LimitExceededNotice info={limitExceededInfo} onDismiss={() => setLimitExceededInfo(null)} />
      {!loading && transactions.length === 0 && <div>No transactions pending back office verifier approval.</div>}
      {!loading && transactions.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
            <thead style={{ background: '#f1f3f7' }}>
              <tr>
                <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Deal Number</th>
                <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Trade Date</th>
                <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Buy/Sell</th>
                <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Counterparty</th>
                <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>Clean Price</th>
                <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>Dirty Price</th>
                <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>Settlement Value</th>
                <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr key={tx.id}>
                  <td style={{ padding: '8px', border: '1px solid #eee' }}>{tx.deal_number || tx.dealNumber}</td>
                  <td style={{ padding: '8px', border: '1px solid #eee' }}>{formatDate(tx.trade_date || tx.tradeDate)}</td>
                  <td style={{ padding: '8px', border: '1px solid #eee', fontWeight: 'bold' }}>
                    {tx.transaction_type === 'Buy' ? 'Buy' : tx.transaction_type === 'Sell' ? 'Sell' : tx.transactionType || 'N/A'}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #eee' }}>{tx.counterparty_name || tx.counterparty || 'N/A'}</td>
                  <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                    {formatPrice(tx.clean_price || tx.cleanPrice)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                    {formatPrice(tx.dirty_price || tx.dirtyPrice)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                    {formatCurrency(tx.settlement_amount || tx.settlementAmount)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button 
                        onClick={() => setViewingTransaction(tx)} 
                        style={{ 
                          padding: '6px 14px', 
                          background: '#007bff', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: 4, 
                          cursor: 'pointer'
                        }}
                      >
                        View
                      </button>
                      <button 
                        onClick={() => setDocumentTransaction(tx)} 
                        style={{ 
                          padding: '6px 14px', 
                          background: '#6c757d', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: 4, 
                          cursor: 'pointer'
                        }}
                        title="View/Upload Documents"
                      >
                        📎 Docs
                      </button>
                      <button 
                        onClick={() => handleApprove(tx.id)} 
                        disabled={approvingId === tx.id || rejectingId === tx.id}
                        style={{ 
                          padding: '6px 14px', 
                          background: '#28a745', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: 4, 
                          cursor: approvingId === tx.id ? 'not-allowed' : 'pointer',
                          opacity: approvingId === tx.id ? 0.6 : 1
                        }}
                      >
                        {approvingId === tx.id ? 'Approving...' : 'Approve'}
                      </button>
                      <button 
                        onClick={() => setRejectingTxId(tx.id)} 
                        disabled={approvingId === tx.id || rejectingId === tx.id}
                        style={{ 
                          padding: '6px 14px', 
                          background: '#dc3545', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: 4, 
                          cursor: rejectingId === tx.id ? 'not-allowed' : 'pointer',
                          opacity: rejectingId === tx.id ? 0.6 : 1
                        }}
                      >
                        Reject
                      </button>
                    </div>
                    {rejectingTxId === tx.id && (
                      <div style={{ marginTop: '8px', padding: '10px', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                        <textarea
                          value={rejectComment}
                          onChange={(e) => setRejectComment(e.target.value)}
                          placeholder="Enter rejection reason..."
                          style={{ width: '100%', minHeight: '60px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '8px' }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleReject(tx.id)}
                            disabled={rejectingId === tx.id || !rejectComment.trim()}
                            style={{
                              padding: '6px 14px',
                              background: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: rejectingId === tx.id || !rejectComment.trim() ? 'not-allowed' : 'pointer',
                              opacity: rejectingId === tx.id || !rejectComment.trim() ? 0.6 : 1
                            }}
                          >
                            {rejectingId === tx.id ? 'Rejecting...' : 'Confirm Reject'}
                          </button>
                          <button
                            onClick={() => {
                              setRejectingTxId(null);
                              setRejectComment('');
                            }}
                            style={{
                              padding: '6px 14px',
                              background: '#6c757d',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <GsecFinalApprovedLettersPanel
          transactions={allRecentTransactions}
          onView={setViewingTransaction}
          valueDateYmd={systemDayYmd || null}
        />
      )}
      
      {viewingTransaction && (
        <DealSlipViewModal
          transaction={viewingTransaction}
          onClose={() => setViewingTransaction(null)}
          dealType="GSec"
          onApprove={async (id) => {
            await handleApprove(id);
            setViewingTransaction(null);
          }}
          onReject={async (id, comment) => {
            setRejectComment(comment);
            await handleReject(id);
            setViewingTransaction(null);
          }}
        />
      )}
      
      {documentTransaction && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          zIndex: 1000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: '8px', 
            padding: '24px', 
            maxWidth: '800px', 
            width: '100%', 
            maxHeight: '90vh', 
            overflow: 'auto' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>
                Documents - Deal #: {documentTransaction.id || documentTransaction.deal_number}
              </h3>
              <button 
                onClick={() => setDocumentTransaction(null)}
                style={{ 
                  padding: '8px 16px', 
                  background: '#6c757d', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 4, 
                  cursor: 'pointer' 
                }}
              >
                Close
              </button>
            </div>
            <DocumentUpload
              transactionType="gsec"
              transactionId={documentTransaction.id || documentTransaction.deal_number}
              onUploadSuccess={() => {}}
              onUploadError={(error) => console.error('Upload error:', error)}
              multiple={true}
            />
            <DocumentList
              transactionType="gsec"
              transactionId={documentTransaction.id || documentTransaction.deal_number}
              onDocumentDeleted={() => {}}
            />
          </div>
        </div>
      )}
    </div>
  );
}
